import { Col, message, Row, Table, Typography } from "antd"
import { useState } from "react"
import PageHeader from "../../../components/Elements/PageHeader"
import { PiUserList } from "react-icons/pi"
import { useCollection } from "../../../hooks/useCollection"
import { useAuthContext } from "../../../hooks/useAuthContext"
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons"
import { TableSearch } from "../../../components/utils/TableSearch"
import EditClientDrawer from "../../../components/Clients/EditClientDrawer"
import ClientDetailDrawer from "../../../components/Clients/ClientDetailDrawer"
import { deleteDoc, doc, updateDoc } from "firebase/firestore"
import { db } from "../../../firebase/config"

const { Text, Link } = Typography

// Create a formatter for Euro currency
const euroFormatter = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "EUR",
})

export default function ClientList() {
  const { user } = useAuthContext()
  const { loading, data } = useCollection("clients", ["user", "==", user.uid])
  const [selectedClient, setSelectedClient] = useState(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const openDrawer = (record) => {
    setSelectedClient(record)
    setDrawerOpen(true)
  }

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, "clients", id))
      message.success("Client deleted.")
      setDrawerOpen(false)
    } catch (err) {
      console.error(err)
      message.error("Failed to delete client.")
    }
  }

  const handleConvertClient = async (record) => {
    const priceRef = doc(db, "clients", record.id)

    try {
      updateDoc(priceRef, {
        tapeClient: false,
      }).then(() => {
        message.success("sent for approval")
      })
    } catch (error) {
      message.error(error)
    }
  }

  const columns = [
    {
      title: "Client",
      dataIndex: "client",
      ...TableSearch("client"),
      render: (value, record) => <Link onClick={() => openDrawer(record)}>{value}</Link>,
    },
    {
      title: "VAT",
      dataIndex: "vat",
      align: "center",
      sorter: {
        compare: (a, b) => a.vat - b.vat,
      },
    },
    {
      title: "Manufacturers",
      dataIndex: ["manufacturer", "company"],
      align: "center",
      sorter: {
        compare: (a, b) => a.manufacturer.company - b.manufacturer.company,
      },
    },
    {
      title: "Contact Email",
      dataIndex: "contactEmail",
      align: "center",
      sorter: {
        compare: (a, b) => a.contactEmail - b.contactEmail,
      },
    },
    {
      title: "Contact Name",
      dataIndex: "contactName",
      align: "center",
      sorter: {
        compare: (a, b) => a.contactName - b.contactName,
      },
    },

    {
      title: "Credit used",
      dataIndex: "orderCredit",
      align: "center",
      render: (value, record) => {
        const creditUsed = Math.round((record.creditUsed || 0) * 100) / 100
        const creditLimit = Math.round((record.creditLimit || 0) * 100) / 100
        return (
          <Text type="success" italic>
            {euroFormatter.format(creditUsed)} / {euroFormatter.format(creditLimit)}
          </Text>
        )
      },
    },
    {
      title: "Apr.",
      dataIndex: "approved",
      defaultSortOrder: "descend",
      width: 50,
      align: "center",
      sorter: (a, b) => a.createdAt - b.createdAt,
      render: (value) =>
        value ? (
          <CheckCircleOutlined style={{ color: "#52c41a" }} />
        ) : (
          <CloseCircleOutlined style={{ color: "#f5222d" }} />
        ),
    },
    {
      title: "Full Client",
      dataIndex: "tapeClient",
      width: 100,
      align: "center",
      render: (value, record) =>
        value ? (
          <Link onClick={() => handleConvertClient(record)}>Convert</Link>
        ) : (
          <Text italic>Full</Text>
        ),
    },
    {
      title: "Action",
      align: "center",
      render: (value, record) => <EditClientDrawer data={record} />,
    },
  ]

  const onChange = (pagination, filters, sorter, extra) => {
    console.log("params", pagination, filters, sorter, extra)
  }

  return (
    <div className="page-padding">
      <Row>
        <Col span={24}>
          <PageHeader text="Client List" icon={<PiUserList />} />
        </Col>
        <Col span={24}>
          <Table
            rowKey={(record) => record.id}
            columns={columns}
            size="small"
            loading={loading}
            dataSource={data}
            pagination={{ defaultPageSize: "50", showSizeChanger: true }}
            onChange={onChange}
          />
        </Col>
      </Row>
      <ClientDetailDrawer
        record={selectedClient}
        type="client"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onDelete={handleDelete}
      />
    </div>
  )
}
